import { useState } from "react";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { ACTIVITY_ACTIONS, ACTIVITY_CATEGORIES, ACTIVITY_TYPES, activityService, RESOURCE_TYPES } from "@/services/analytics/ActivityService";

export const PasswordSection = () => {
  const { user } = useAuth();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long.");
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;

      await activityService.logActivity({
        activityType: ACTIVITY_TYPES.USER_ACTION,
        action: ACTIVITY_ACTIONS.PASSWORD_CHANGED,
        title: `Changed password`,
        description: `Updated account password for enhanced security`,
        resourceType: RESOURCE_TYPES.USER_ACCOUNT,
        resourceId: user?.id || "",
        resourceName: "Account Security",
        category: ACTIVITY_CATEGORIES.SECURITY,
        metadata: {
          security_action: "password_change",
          timestamp: new Date().toISOString(),
        },
      });

      toast.success("Password updated successfully!");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      toast.error(`Failed to update password: ${err.message}`);
    }
  };

  return (
    <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)]">
      <div className="flex items-center mb-[18px]">
        <div className="h-8 w-8 rounded-full bg-genie-primary/10 flex items-center justify-center mr-3">
          <Lock className="h-5 w-5 text-genie-primary" />
        </div>
        <h2 className="text-xl font-semibold">Change Password</h2>
      </div>
      <form onSubmit={handleUpdatePassword} className="flex flex-col gap-[14px]">
        <div>
          <label htmlFor="new-password" className="block mb-1.5 font-medium text-sm">
            New Password
          </label>
          <input
            id="new-password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full p-[9px] rounded-md border border-gray-300 text-sm"
          />
        </div>
        <div>
          <label htmlFor="confirm-password" className="block mb-1.5 font-medium text-sm">
            Confirm New Password
          </label>
          <input
            id="confirm-password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full p-[9px] rounded-md border border-gray-300 text-sm"
          />
        </div>
        <button
          type="submit"
          className="self-start text-white border-none rounded-md px-3.5 py-[7px] font-semibold cursor-pointer text-sm bg-genie-primary"
        >
          Update Password
        </button>
      </form>
    </div>
  );
};